import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import type { ReactNode } from "react";
import { FeeScheduleTableCellText } from "_shared/fee-schedules/categories/_views/fee-schedule-table-cell";
import { TEAM_LEADER_ROLE, type TeamRosterCell } from "_shared/entity-members/_views/team-roster-table";

/**
 * One way to change who leads a team, offered in that team's leader row (#6260). `label` is the short
 * link the narrow Role column has room for; `ariaLabel` names the whole action, which is also the
 * title of the dialog it opens.
 */
export type TeamLeaderRoleAction = {
  key: string;
  label: string;
  ariaLabel: string;
  disabled?: boolean;
  onClick: () => void;
};

const RoleStack = styled("div")(() => ({
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
}));

const RoleLink = styled(Link)(({ theme }) => ({
  ...theme.typography.caption,
  textAlign: "left",
  whiteSpace: "nowrap",
  "&:disabled": {
    color: theme.palette.text.disabled,
    cursor: "default",
    textDecoration: "none",
  },
})) as typeof Link;

/**
 * The leader row's Role cell: the role, with the handover actions the reader may take under it.
 *
 * Stacked rather than side by side: the Role column is sized for the word "Leader", and a link beside
 * it would be cut off at the column's edge. With no actions it is the plain role every other row has.
 *
 * `roleControl` replaces the word "Leader" with a control that changes the role, such as the Role
 * select a draft's leader has (#6861). The actions still stack under it.
 */
export function teamLeaderRoleCell(
  actions: readonly TeamLeaderRoleAction[],
  roleControl?: ReactNode,
): TeamRosterCell {
  if (actions.length === 0 && !roleControl) return { content: TEAM_LEADER_ROLE };
  if (actions.length === 0) return { searchText: TEAM_LEADER_ROLE, sortText: TEAM_LEADER_ROLE, content: roleControl };
  return {
    searchText: TEAM_LEADER_ROLE,
    content: (
      <RoleStack>
        {roleControl ?? <FeeScheduleTableCellText>{TEAM_LEADER_ROLE}</FeeScheduleTableCellText>}
        {actions.map((action) => (
          <RoleLink
            key={action.key}
            component="button"
            type="button"
            underline="hover"
            aria-label={action.ariaLabel}
            disabled={action.disabled}
            onClick={action.onClick}
          >
            {action.label}
          </RoleLink>
        ))}
      </RoleStack>
    ),
  };
}
